import React, { useCallback, useMemo } from 'react';

import { IntegrationDomain, IntegrationVersion } from 'api/usePublisherIntegrationTest';

import { IntegrationTestModal } from 'components/IntegrationTestModal/IntegrationTestModal';

import { IIntegrationSectionDependencies } from '../DeveloperSettings.integration';
import { EApiVersion, ESavingForm } from '../DeveloperSettings.types';
import {
  determineSelectedAwardingVersion,
  resolveIntegrationVersion
} from '../helpers/developerSettings.helpers';
import { buildGrantAwardVersionConfigs } from '../helpers/versionConfigs.helpers';
import { useApiTestingAnalytics } from '../hooks/useApiTestingAnalytics';
import { ISaveResult, useGrantAwardForm } from '../hooks/useCallbackForms';
import { useGrantAwardTestModal } from '../hooks/useGrantAwardTestModal';
import { useIntegrationTestModal } from '../hooks/useIntegrationTestModal';

import { CallbackSection } from './CallbackSection';

interface IGrantAwardSectionProps extends IIntegrationSectionDependencies {
  isAwardingV2FeatureFlagOn: boolean;
}

const resolveGrantAwardVersion = (
  isAwardingV2FeatureFlagOn: boolean,
  awardingV2Ratio: number | undefined
): IntegrationVersion => {
  const selectedVersion = determineSelectedAwardingVersion(awardingV2Ratio);
  return resolveIntegrationVersion(isAwardingV2FeatureFlagOn, selectedVersion);
};

export const GrantAwardSection: React.FC<IGrantAwardSectionProps> = ({
  publisherId,
  integrationData,
  updateIntegration,
  getIntegration,
  savingForm,
  setSavingForm,
  isProfilesEnabled,
  hasCategories,
  isWebStore,
  isWebCheckout,
  setTaskStatus,
  isAwardingV2FeatureFlagOn
}) => {
  const resolvedVersion = useMemo(
    () => resolveGrantAwardVersion(isAwardingV2FeatureFlagOn, integrationData?.awardingV2Ratio),
    [isAwardingV2FeatureFlagOn, integrationData?.awardingV2Ratio]
  );

  const analytics = useApiTestingAnalytics({
    domain: IntegrationDomain.AWARD,
    version: resolvedVersion
  });

  const handleSaveResult = useCallback(
    ({ status, url, version: savedFormVersion }: ISaveResult) => {
      const savedIntegrationVersion = resolveIntegrationVersion(
        isAwardingV2FeatureFlagOn,
        savedFormVersion || EApiVersion.V1
      );
      analytics.trackIntegrationSaved({
        url,
        status,
        api: IntegrationDomain.AWARD,
        version: savedIntegrationVersion
      });
    },
    [analytics, isAwardingV2FeatureFlagOn]
  );

  const form = useGrantAwardForm({
    integrationData,
    updateIntegration,
    getIntegration,
    hasCategories,
    isWebStore,
    isWebCheckout,
    setTaskStatus,
    setSavingForm,
    onSaveResult: handleSaveResult
  });

  const testModalData = useGrantAwardTestModal({
    grantAwardForm: form,
    isAwardingV2FeatureFlagOn
  });

  const testModal = useIntegrationTestModal({
    publisherId,
    domain: IntegrationDomain.AWARD,
    version: testModalData.version,
    title: 'Grant Award Callback Test',
    callbackUrl: testModalData.callbackUrl,
    onTestResult: analytics.trackTestExecuted
  });

  const { openModal } = testModal;

  const handleOpenModal = useCallback(() => {
    analytics.trackTestModalOpened({ url: testModalData.callbackUrl });
    openModal();
  }, [analytics, testModalData.callbackUrl, openModal]);

  const versionConfigs = useMemo(
    () => buildGrantAwardVersionConfigs(form, isAwardingV2FeatureFlagOn),
    [form, isAwardingV2FeatureFlagOn]
  );

  const { schema, ...testModalProps } = testModal;

  return (
    <>
      <CallbackSection
        title="Grant Award Callback"
        versionConfigs={versionConfigs}
        onSave={form.submitForm}
        isSaveDisabled={!form.dirty || savingForm !== ESavingForm.NONE}
        isLoading={savingForm === ESavingForm.GRANT_AWARD}
        disabled={isProfilesEnabled}
        onTestClick={handleOpenModal}
        testButtonDisabled={!testModalData.callbackUrl}
        onDocsClick={analytics.trackDocsClicked}
      />
      {schema && (
        <IntegrationTestModal
          {...testModalProps}
          schema={schema}
          schemaSelectorLabel="Order Type"
        />
      )}
    </>
  );
};
